import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/adminAuth';
import { toast } from 'sonner';
import {
  FileText, Plus, Check, X, RefreshCw, Search, ArrowRight,
  Receipt, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { formatDate, getTodayLocal } from '../lib/dateUtils';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Create Credit Note Modal
function CreateCreditNoteModal({ open, onClose, clients, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    amount: '',
    reason: '',
    date: getTodayLocal()
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.amount || !formData.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      await adminApi.post('/api/admin/accounting/credit-notes', {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Credit note created successfully');
      onSuccess();
      onClose();
      setFormData({ client_id: '', amount: '', reason: '', date: getTodayLocal() });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create credit note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            Create Credit Note
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-neutral-700">Client *</label>
            <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c._id} value={c._id}>
                    {c.company_name || c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700">Amount (₹) *</label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              placeholder="Enter amount"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700">Date</label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700">Reason *</label>
            <Textarea
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Reason for credit note"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Credit Note
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Apply Credit Note Modal
function ApplyCreditNoteModal({ open, onClose, creditNote, invoices, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [amount, setAmount] = useState('');

  // Filter invoices for same client with outstanding balance
  const eligibleInvoices = invoices.filter(inv => 
    inv.client_id === creditNote?.client_id && 
    inv.status !== 'paid' && 
    inv.status !== 'cancelled' &&
    (inv.total - (inv.amount_received || 0)) > 0
  );

  const handleApply = async () => {
    if (!selectedInvoice || !amount) {
      toast.error('Please select invoice and enter amount');
      return;
    }

    try {
      setLoading(true);
      await adminApi.post(`/api/admin/accounting/credit-notes/${creditNote.id}/apply`, {
        invoice_id: selectedInvoice,
        amount: parseFloat(amount)
      });
      toast.success('Credit note applied successfully');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply credit note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-teal-600" />
            Apply Credit Note
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-teal-50 p-4 rounded-xl">
            <p className="text-sm text-teal-700">
              <strong>{creditNote?.credit_note_number}</strong>
            </p>
            <p className="text-lg font-bold text-teal-800">{formatCurrency(creditNote?.balance)} available</p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-neutral-700">Apply to Invoice</label>
            <Select value={selectedInvoice} onValueChange={setSelectedInvoice}>
              <SelectTrigger>
                <SelectValue placeholder="Select invoice" />
              </SelectTrigger>
              <SelectContent>
                {eligibleInvoices.map(inv => (
                  <SelectItem key={inv._id} value={inv._id}>
                    {inv.invoice_number} - Outstanding: {formatCurrency(inv.total - (inv.amount_received || 0))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {eligibleInvoices.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">No unpaid invoices for this client</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">Amount to Apply (₹)</label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              max={creditNote?.balance}
              min="0"
              step="0.01"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleApply} disabled={loading || !selectedInvoice || !amount}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            Apply Credit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Credit Notes Component
export default function CreditNotes() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCN, setSelectedCN] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [cnRes, clientsRes, invoicesRes] = await Promise.all([
        adminApi.get('/api/admin/accounting/credit-notes'),
        adminApi.get('/api/admin/clients'),
        adminApi.get('/api/admin/accounting/invoices', { params: { limit: 10000 } })
      ]);
      setCreditNotes(cnRes.data.credit_notes || []);
      setClients(clientsRes.data || []);
      const invoicesData = invoicesRes.data?.invoices || invoicesRes.data || [];
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredCreditNotes = creditNotes.filter(cn =>
    cn.credit_note_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cn.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusColors = {
    issued: 'bg-blue-100 text-blue-700',
    partially_applied: 'bg-amber-100 text-amber-700',
    fully_applied: 'bg-emerald-100 text-emerald-700'
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="credit-notes-loading">
        <HeaderSkeleton />
        <MetricCardsSkeleton count={3} />
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credit-notes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Credit Notes</h1>
          <p className="text-sm text-neutral-500">Manage credit notes and adjustments</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-credit-note-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Credit Note
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          placeholder="Search credit notes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass-card p-4">
          <p className="text-sm text-neutral-500">Total Credit Notes</p>
          <p className="text-2xl font-bold text-[#1a1a2e]">{creditNotes.length}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
          <p className="text-sm text-blue-600">Total Issued</p>
          <p className="text-2xl font-bold text-blue-700">
            {formatCurrency(creditNotes.reduce((sum, cn) => sum + cn.amount, 0))}
          </p>
        </div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
          <p className="text-sm text-emerald-600">Available Balance</p>
          <p className="text-2xl font-bold text-emerald-700">
            {formatCurrency(creditNotes.reduce((sum, cn) => sum + (cn.balance || 0), 0))}
          </p>
        </div>
      </div>

      {/* Credit Notes Table */}
      <div className="soft-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/40 border-b border-white/40">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Credit Note #</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Date</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Amount</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Balance</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">Status</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {filteredCreditNotes.map((cn) => (
                <tr key={cn.id} className="hover:bg-white/60">
                  <td className="px-4 py-3 font-medium text-[#1a1a2e]">{cn.credit_note_number}</td>
                  <td className="px-4 py-3 text-neutral-700">{cn.client_name}</td>
                  <td className="px-4 py-3 text-neutral-500">{cn.date ? formatDate(cn.date) : '-'}</td>
                  <td className="px-4 py-3 text-right font-medium text-[#1a1a2e]">{formatCurrency(cn.amount)}</td>
                  <td className="px-4 py-3 text-right font-medium text-teal-600">{formatCurrency(cn.balance)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[cn.status] || 'bg-neutral-100 text-neutral-600'}`}>
                      {cn.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {cn.balance > 0 && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { setSelectedCN(cn); setShowApplyModal(true); }}
                      >
                        Apply
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {filteredCreditNotes.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-neutral-500">
                    No credit notes found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <CreateCreditNoteModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        clients={clients}
        onSuccess={fetchData}
      />
      
      <ApplyCreditNoteModal
        open={showApplyModal}
        onClose={() => { setShowApplyModal(false); setSelectedCN(null); }}
        creditNote={selectedCN}
        invoices={invoices}
        onSuccess={fetchData}
      />
    </div>
  );
}
